import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const navLinks = [
  { href: '#about', label: 'About' },
  { href: '#services', label: 'Services' },
  { href: '#expertise', label: 'Expertise' },
  { href: '#why-vantaris', label: 'Why Vantaris' },
  { href: '#insights', label: 'Insights' },
  { href: '#contact', label: 'Contact' },
];

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAnchor = (e, hash) => {
    e.preventDefault();
    setIsOpen(false);
    if (location.pathname !== '/') {
      navigate('/' + hash);
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#1B0E33]/92 backdrop-blur-md border-b border-white/5 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.5)]'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20">
          {/* Wordmark */}
          <Link
            to="/"
            data-testid="nav-logo"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="flex items-center gap-3 group"
          >
            <img
              src="/brand/vantaris-icon.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-9 md:h-10 md:w-10 object-contain"
            />
            <span className="flex items-baseline gap-2">
              <span className="font-display text-xl md:text-2xl font-semibold text-white tracking-tight">
                Vantaris
              </span>
              <span className="hidden sm:inline text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-medium">
                Integrated Solutions
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-9">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchor(e, link.href)}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-[13px] font-medium tracking-[0.05em] uppercase text-slate-300 hover:text-[#E5C75B] transition-colors duration-300 whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center">
            <a href="#contact" onClick={(e) => handleAnchor(e, '#contact')}>
              <Button
                data-testid="nav-consultation-btn"
                className="bg-[#D4AF37] text-[#1B0E33] hover:bg-[#E5C75B] px-5 py-2.5 h-auto rounded-sm text-[12px] font-semibold uppercase tracking-[0.1em] transition-all"
              >
                Schedule a Consultation
              </Button>
            </a>
          </div>

          {/* Mobile menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-toggle"
                className="text-white hover:bg-white/10"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80 bg-[#1B0E33] border-white/10 text-white">
              <div className="flex flex-col gap-7 mt-12">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleAnchor(e, link.href)}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-base font-medium tracking-[0.08em] uppercase text-slate-200 hover:text-[#E5C75B] transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
                <div className="pt-6 border-t border-white/10">
                  <a href="#contact" onClick={(e) => handleAnchor(e, '#contact')}>
                    <Button
                      data-testid="mobile-consultation-btn"
                      className="w-full bg-[#D4AF37] text-[#1B0E33] hover:bg-[#E5C75B] py-3 rounded-sm text-sm font-semibold uppercase tracking-[0.1em]"
                    >
                      Schedule a Consultation
                    </Button>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navigation;
